/**
 * Enterprise Cart API - Cart-Specific Module
 *
 * Extends EnterpriseAPIClient to provide unified cart operations:
 * - Add to cart (single/multiple items)
 * - Update quantities
 * - Remove items
 * - Get cart state
 * - Property normalization
 * - Automatic event dispatching
 * - Automatic cart drawer management
 *
 * Part of Zanova Enterprise Architecture - Skill #2
 *
 * REPLACES: 300+ lines of duplicate cart logic across 6 files:
 * - product-form.js
 * - size-overlay-add-to-cart.js
 * - wishlist.js
 * - enterprise-wishlist-drawer.js
 * - custom-cart-drawer.js
 * - cart.js
 */

class EnterpriseCartAPI extends EnterpriseAPIClient {
  constructor(config = {}) {
    super(config);

    // Cart-specific configuration
    this.cartConfig = {
      autoOpenDrawer: config.autoOpenDrawer !== false, // true by default
      autoUpdateIcon: config.autoUpdateIcon !== false, // true by default
      autoDispatchEvents: config.autoDispatchEvents !== false, // true by default
      showSuccessAnimation: config.showSuccessAnimation !== false, // true by default
      ...config
    };

    // Cart endpoints
    this.endpoints = {
      add: '/cart/add.js',
      get: '/cart.js',
      update: '/cart/update.js',
      change: '/cart/change.js',
      clear: '/cart/clear.js'
    };

    this.log('EnterpriseCartAPI initialized', this.cartConfig);
  }

  /**
   * Add single item to cart
   *
   * @param {string|number} variantId - Product variant ID
   * @param {number} quantity - Quantity to add (default: 1)
   * @param {object} options - Additional options
   * @returns {Promise} - Cart response
   *
   * Options:
   * - properties: Custom properties object (e.g., {_compare_at_price: '8000'})
   * - compareAtPrice: Auto-adds _compare_at_price property (in cents)
   * - openDrawer: Whether to open cart drawer after success (default: true)
   * - showSuccess: Whether to show success state (default: true)
   * - dispatchEvents: Whether to dispatch cart:updated event (default: true)
   */
  async addToCart(variantId, quantity = 1, options = {}) {
    const {
      properties = {},
      compareAtPrice = null,
      openDrawer = this.cartConfig.autoOpenDrawer,
      showSuccess = this.cartConfig.showSuccessAnimation,
      dispatchEvents = this.cartConfig.autoDispatchEvents,
      ...otherOptions
    } = options;

    this.log('Adding item to cart', { variantId, quantity, properties });

    // Normalize properties
    const normalizedProperties = this._normalizeProperties(properties, compareAtPrice);

    // Build request body
    const body = {
      id: String(variantId),
      quantity: parseInt(quantity, 10),
      properties: normalizedProperties
    };

    try {
      window.debugLogger.log('🚀 addToCart: Starting add to cart process', { variantId, quantity });

      // Make API request - returns the ITEM, not full cart
      const itemResponse = await this.post(this.endpoints.add, body, {
        ...otherOptions,
        cache: false // Never cache cart add requests
      });

      window.debugLogger.log('✅ addToCart: POST /cart/add.js succeeded', { itemResponse });

      // CRITICAL FIX: Fetch full cart to get item_count
      // The /cart/add.js response only returns the item, not the full cart state
      const fullCart = await this.getCart({ cache: false });

      window.debugLogger.log('✅ addToCart: GET /cart.js succeeded', {
        itemCount: fullCart.item_count,
        itemsLength: fullCart.items?.length
      });

      // Track GA4 add_to_cart event (Phase B1)
      // FIX: itemResponse from /cart/add.js may be empty/malformed
      // Instead, find the item in fullCart.items that matches the variantId we just added
      window.debugLogger.log('🔍 GA4 Tracking Debug - Start', {
        hasGA4Ecommerce: !!window.ga4Ecommerce,
        hasFullCart: !!fullCart,
        hasItems: !!(fullCart && fullCart.items),
        itemsCount: fullCart?.items?.length,
        variantId: variantId,
        variantIdType: typeof variantId
      });

      if (window.ga4Ecommerce && fullCart && fullCart.items) {
        window.debugLogger.log('🔍 GA4 Tracking - Conditions passed, searching for item...');
        const addedItem = fullCart.items.find(item =>
          item.id === parseInt(variantId) ||
          item.variant_id === parseInt(variantId)
        );

        window.debugLogger.log('🔍 GA4 Tracking - Search result:', {
          foundItem: !!addedItem,
          itemId: addedItem?.id,
          itemVariantId: addedItem?.variant_id,
          itemSku: addedItem?.sku
        });

        if (addedItem) {
          this.log('Tracking add_to_cart for item:', addedItem.sku || addedItem.id);
          window.debugLogger.log('🎯 GA4 Tracking - Calling trackAddToCart with:', addedItem);
          try {
            window.ga4Ecommerce.trackAddToCart(addedItem);
            window.debugLogger.log('✅ GA4 Tracking - trackAddToCart completed successfully');
          } catch (trackingError) {
            window.debugLogger.error('❌ GA4 Tracking - Error in trackAddToCart:', trackingError);
          }
        } else {
          this.log('Warning: Could not find added item in cart for GA4 tracking');
          window.debugLogger.log('❌ GA4 Tracking - Item not found in cart. Cart items:', fullCart.items);
        }
      } else {
        window.debugLogger.log('❌ GA4 Tracking - Conditions failed:', {
          noGA4Ecommerce: !window.ga4Ecommerce,
          noFullCart: !fullCart,
          noItems: !(fullCart && fullCart.items)
        });
      }

      // Handle successful response with FULL cart data
      window.debugLogger.log('🎯 addToCart: About to call _handleCartSuccess');
      await this._handleCartSuccess(fullCart, {
        openDrawer,
        showSuccess,
        dispatchEvents
      });

      window.debugLogger.log('✅ addToCart: Completed successfully, returning fullCart');
      return fullCart;

    } catch (error) {
      window.debugLogger.error('❌ addToCart: Error caught in try-catch', error);
      this.log('Failed to add item to cart', error);
      throw this._normalizeCartError(error);
    }
  }

  /**
   * Add multiple items to cart
   *
   * @param {Array} items - Array of items [{id, quantity, properties}]
   * @param {object} options - Additional options
   * @returns {Promise} - Cart response
   */
  async addMultipleToCart(items, options = {}) {
    const {
      openDrawer = this.cartConfig.autoOpenDrawer,
      showSuccess = this.cartConfig.showSuccessAnimation,
      dispatchEvents = this.cartConfig.autoDispatchEvents,
      ...otherOptions
    } = options;

    this.log('Adding multiple items to cart', { count: items.length });

    // Normalize each item's properties
    const normalizedItems = items.map(item => ({
      id: String(item.id || item.variant),
      quantity: parseInt(item.quantity || 1, 10),
      properties: this._normalizeProperties(
        item.properties || {},
        item.compareAtPrice || item.compare_at_price
      )
    }));

    const body = { items: normalizedItems };

    try {
      // Make API request - returns items array, not full cart
      const itemsResponse = await this.post(this.endpoints.add, body, {
        ...otherOptions,
        cache: false
      });

      // CRITICAL FIX: Fetch full cart to get item_count
      const fullCart = await this.getCart({ cache: false });

      // Track GA4 add_to_cart events for each item (Phase B1)
      // FIX: itemsResponse from /cart/add.js may be empty/malformed
      // Instead, find items in fullCart.items that match the variant IDs we just added
      if (window.ga4Ecommerce && fullCart && fullCart.items) {
        const addedVariantIds = normalizedItems.map(item => parseInt(item.id));
        fullCart.items.forEach(cartItem => {
          if (addedVariantIds.includes(cartItem.id) || addedVariantIds.includes(cartItem.variant_id)) {
            this.log('Tracking add_to_cart for item:', cartItem.sku || cartItem.id);
            window.ga4Ecommerce.trackAddToCart(cartItem);
          }
        });
      }

      await this._handleCartSuccess(fullCart, {
        openDrawer,
        showSuccess,
        dispatchEvents
      });

      return fullCart;

    } catch (error) {
      this.log('Failed to add multiple items to cart', error);
      throw this._normalizeCartError(error);
    }
  }

  /**
   * Get current cart state
   *
   * @param {object} options - Additional options
   * @returns {Promise} - Cart data
   */
  async getCart(options = {}) {
    const { cache = true, ...otherOptions } = options;

    try {
      const cart = await this.get(this.endpoints.get, {
        ...otherOptions,
        cache
      });

      // Update AppStore (Skill #3 - Enterprise State Management)
      if (window.appStore && cart) {
        window.appStore.updateCart(cart);
      }

      this.log('Retrieved cart', { itemCount: cart.item_count });
      return cart;

    } catch (error) {
      this.log('Failed to get cart', error);
      throw this._normalizeCartError(error);
    }
  }

  /**
   * Update item quantity in cart
   *
   * @param {string|number} lineItemKey - Line item key or variant ID
   * @param {number} quantity - New quantity (0 to remove)
   * @param {object} options - Additional options
   * @returns {Promise} - Updated cart
   */
  async updateQuantity(lineItemKey, quantity, options = {}) {
    const {
      sections = null,
      sectionsUrl = null,
      dispatchEvents = this.cartConfig.autoDispatchEvents,
      ...otherOptions
    } = options;

    this.log('Updating item quantity', { lineItemKey, quantity });

    const body = {
      id: String(lineItemKey),
      quantity: parseInt(quantity, 10)
    };

    // Add sections if provided (for section-based updates)
    if (sections) {
      body.sections = sections;
      body.sections_url = sectionsUrl;
    }

    try {
      const response = await this.post(this.endpoints.change, body, {
        ...otherOptions,
        cache: false
      });

      if (dispatchEvents) {
        this._dispatchCartUpdate(response);
      }

      return response;

    } catch (error) {
      this.log('Failed to update quantity', error);
      throw this._normalizeCartError(error);
    }
  }

  /**
   * Remove item from cart (sets quantity to 0)
   *
   * @param {string|number} lineItemKey - Line item key
   * @param {object} options - Additional options
   * @returns {Promise} - Updated cart
   */
  async removeItem(lineItemKey, options = {}) {
    this.log('Removing item from cart', { lineItemKey });
    return this.updateQuantity(lineItemKey, 0, options);
  }

  /**
   * Update multiple items at once
   *
   * @param {object} updates - Object mapping variant IDs to quantities {variantId: quantity}
   * @param {object} options - Additional options
   * @returns {Promise} - Updated cart
   */
  async updateMultiple(updates, options = {}) {
    const {
      sections = null,
      sectionsUrl = null,
      dispatchEvents = this.cartConfig.autoDispatchEvents,
      ...otherOptions
    } = options;

    this.log('Updating multiple items', updates);

    const body = { updates };

    if (sections) {
      body.sections = sections;
      body.sections_url = sectionsUrl;
    }

    try {
      const response = await this.post(this.endpoints.update, body, {
        ...otherOptions,
        cache: false
      });

      if (dispatchEvents) {
        this._dispatchCartUpdate(response);
      }

      return response;

    } catch (error) {
      this.log('Failed to update multiple items', error);
      throw this._normalizeCartError(error);
    }
  }

  /**
   * Update cart note
   *
   * @param {string} note - Cart note
   * @param {object} options - Additional options
   * @returns {Promise} - Updated cart
   */
  async updateNote(note, options = {}) {
    this.log('Updating cart note', { note });

    const body = { note };

    try {
      const response = await this.post(this.endpoints.update, body, {
        ...options,
        cache: false
      });

      this._dispatchCartUpdate(response);
      return response;

    } catch (error) {
      this.log('Failed to update cart note', error);
      throw this._normalizeCartError(error);
    }
  }

  /**
   * Clear entire cart
   *
   * @param {object} options - Additional options
   * @returns {Promise} - Empty cart
   */
  async clear(options = {}) {
    this.log('Clearing cart');

    try {
      const response = await this.post(this.endpoints.clear, {}, {
        ...options,
        cache: false
      });

      this._dispatchCartUpdate(response);
      return response;

    } catch (error) {
      this.log('Failed to clear cart', error);
      throw this._normalizeCartError(error);
    }
  }

  /**
   * PRIVATE METHODS
   */

  /**
   * Normalize properties object
   * Handles _compare_at_price extraction and formatting
   */
  _normalizeProperties(properties = {}, compareAtPrice = null) {
    const normalized = { ...properties };

    // Add compare_at_price if provided and not already set
    if (compareAtPrice && !normalized._compare_at_price) {
      // Ensure it's a string and remove any non-numeric characters except digits
      const priceValue = String(compareAtPrice).replace(/[^0-9]/g, '');
      if (priceValue) {
        normalized._compare_at_price = priceValue;
      }
    }

    return normalized;
  }

  /**
   * Handle successful cart response
   */
  async _handleCartSuccess(response, options) {
    const { openDrawer, showSuccess, dispatchEvents } = options;

    // Track successful cart operation in PostHog (Phase B2: Analytics)
    if (typeof window.posthog !== 'undefined') {
      window.posthog.capture('cart_operation_success', {
        item_count: response.item_count || 0,
        total_price: (response.total_price / 100) || 0, // Convert cents to dollars
        currency: response.currency || 'USD',
        operation: 'cart_update'
      });
    }

    // Dispatch cart:updated event
    if (dispatchEvents) {
      this._dispatchCartUpdate(response);
    }

    // Update cart icon count - FIXED: Pass response to avoid race condition
    if (this.cartConfig.autoUpdateIcon) {
      await this._updateCartIcon(response);
    }

    // Open cart drawer
    if (openDrawer && window.customCartDrawer) {
      setTimeout(() => {
        window.customCartDrawer.open();
      }, 100); // Small delay to ensure cart is updated
    }

    return response;
  }

  /**
   * Dispatch cart:updated event
   */
  _dispatchCartUpdate(cartData) {
    // Update AppStore (Skill #3 - Enterprise State Management)
    if (window.appStore) {
      window.appStore.updateCart(cartData);
    }

    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart: cartData }
    }));

    this.log('Dispatched cart:updated event', { itemCount: cartData.item_count });
  }

  /**
   * Update cart icon count
   * @param {object} cartData - Optional cart data (if not provided, fetches from API)
   */
  async _updateCartIcon(cartData = null) {
    try {
      // Use provided cart data or fetch from API
      const cart = cartData || await this.getCart({ cache: false });
      const cartCount = cart.item_count || 0;

      // Update cart count bubble
      const cartCountElement = document.querySelector('.cart-count-bubble');
      if (cartCountElement) {
        // Update the span INSIDE the bubble (don't overwrite the container!)
        const countSpan = cartCountElement.querySelector('span[aria-hidden="true"]');
        if (countSpan) {
          countSpan.textContent = cartCount;
        }

        // Show/hide the bubble
        cartCountElement.style.display = cartCount > 0 ? 'flex' : 'none';
      }

      this.log('Updated cart icon', { count: cartCount });

    } catch (error) {
      this.log('Failed to update cart icon', error);
    }
  }

  /**
   * Normalize cart errors to user-friendly messages
   */
  _normalizeCartError(error) {
    // Preserve original error
    const normalized = new Error();
    normalized.originalError = error;

    // Determine user-friendly message
    if (error.status === 422) {
      normalized.message = 'Sorry, this item is currently out of stock or unavailable.';
      normalized.code = 'OUT_OF_STOCK';
    } else if (error.status === 404) {
      normalized.message = 'Sorry, this product could not be found.';
      normalized.code = 'NOT_FOUND';
    } else if (error.name === 'AbortError') {
      normalized.message = 'Request was cancelled.';
      normalized.code = 'ABORTED';
    } else if (!navigator.onLine) {
      normalized.message = 'No internet connection. Please check your network.';
      normalized.code = 'NETWORK_ERROR';
    } else {
      normalized.message = 'Sorry, something went wrong. Please try again.';
      normalized.code = 'UNKNOWN_ERROR';
    }

    normalized.status = error.status;

    // Report to PostHog (Phase B2: Error Tracking)
    // Only report actionable errors (not user-caused issues like network/abort)
    if (typeof window.posthog !== 'undefined' &&
        normalized.code !== 'ABORTED' &&
        normalized.code !== 'NETWORK_ERROR') {

      window.posthog.capture('cart_error', {
        error_code: normalized.code,
        error_message: normalized.message,
        http_status: error.status || 'unknown',
        request_url: error.url || 'unknown',
        cart_item_count: this._cartData?.item_count || 0,
        error_level: normalized.code === 'OUT_OF_STOCK' ? 'warning' : 'error'
      });
    }

    return normalized;
  }
}

// Export for use in theme
if (typeof window !== 'undefined') {
  window.EnterpriseCartAPI = EnterpriseCartAPI;

  // Create global cart API instance
  window.cartAPI = new EnterpriseCartAPI({
    enableLogging: false // Disabled in production
  });
}
